import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import GreenButton from '../Components/Buttons/Greenbutton';
import { useCart } from '../context/CartContext';
import { getProducts } from '../lib/api';
import { formatNpr } from '../lib/currency';
import { getOptimizedImageUrl } from '../lib/imageUrl';
import { getProductDisplayId } from '../lib/productDisplayId';

function OffersPage() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingProductId, setAddingProductId] = useState('');
  const [timeLeft, setTimeLeft] = useState(24 * 60 * 60);
  const { addItem } = useCart();

  useEffect(() => {
    function updateTimeLeft() {
      const now = new Date();
      const nextDay = new Date(now);
      nextDay.setHours(24, 0, 0, 0);
      const seconds = Math.max(0, Math.floor((nextDay.getTime() - now.getTime()) / 1000));
      setTimeLeft(seconds);
    }

    updateTimeLeft();
    const intervalId = window.setInterval(updateTimeLeft, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadOffers() {
      try {
        const nextProducts = await getProducts();
        if (isMounted) {
          setProducts(nextProducts.filter((product) => product.featured).slice(0, 3));
          setError('');
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadOffers();

    return () => {
      isMounted = false;
    };
  }, []);

  async function handleAddToCart(productId) {
    setAddingProductId(productId);

    try {
      await addItem(productId, 1);
    } catch (_error) {
      // CartContext handles visible errors.
    } finally {
      window.setTimeout(() => {
        setAddingProductId('');
      }, 700);
    }
  }

  const hoursLeft = String(Math.floor(timeLeft / 3600)).padStart(2, '0');
  const minutesLeft = String(Math.floor((timeLeft % 3600) / 60)).padStart(2, '0');
  const secondsLeft = String(timeLeft % 60).padStart(2, '0');

  return (
    <section className="page-card offers-page">
      <div className="offers-page__hero">
        <p className="products-page__crumb">Home / Offer / Today</p>
        <h1 className="page-card__title">Today&apos;s Offers</h1>
        <p className="page-card__text">
          Hand-picked frames at a flat 50% off — today only, while the timer runs.
        </p>
      </div>

      <section className="products-offers">
        <div className="products-offers__simple">
          <div>
            <p className="products-offers__eyebrow">Today Offer</p>
            <h2>Offer products with 50% discount for 24 hours.</h2>
          </div>
          <div className="offers-countdown" role="timer" aria-label="Offer time remaining">
            <div className="offers-countdown__cell">
              <strong>{hoursLeft}</strong>
              <span>hrs</span>
            </div>
            <em>:</em>
            <div className="offers-countdown__cell">
              <strong>{minutesLeft}</strong>
              <span>min</span>
            </div>
            <em>:</em>
            <div className="offers-countdown__cell">
              <strong>{secondsLeft}</strong>
              <span>sec</span>
            </div>
          </div>
        </div>
      </section>

      {isLoading ? <p className="products-state">Loading offers...</p> : null}
      {error ? <p className="products-state products-state--error">{error}</p> : null}
      {!isLoading && !error && products.length === 0 ? (
        <p className="products-state">No offers are running right now — check back tomorrow.</p>
      ) : null}

      <div className="products-offers__row">
        {products.map((product) => {
          const discountedPrice = Number(product.price) * 0.5;

          return (
            <article key={product.id} className="products-offer-mini">
              <Link className="products-offer-mini__image" to={`/products/${product.id}`}>
                <img
                  src={getOptimizedImageUrl(product.image, 600)}
                  alt={product.name}
                  loading="lazy"
                  decoding="async"
                />
              </Link>
              <div className="products-offer-mini__content">
                <p className="products-offer-mini__tag">50% OFF</p>
                <p className="product-card__id">Product ID: {getProductDisplayId(product.id)}</p>
                <h3>
                  <Link to={`/products/${product.id}`}>{product.name}</Link>
                </h3>
                <div className="products-offer-mini__pricing">
                  <span className="product-card__price-old">{formatNpr(product.price)}</span>
                  <strong>{formatNpr(discountedPrice)}</strong>
                </div>
                <div className="product-card__actions">
                  <GreenButton
                    text={addingProductId === product.id ? 'Added' : 'Add to Cart'}
                    onClick={() => {
                      void handleAddToCart(product.id);
                    }}
                  />
                  <Link className="product-card__link" to={`/products/${product.id}`}>
                    View
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default OffersPage;
